import { useState } from 'react';  
import { Button } from '../ui/button';  
import { Input } from '../ui/input';  
import { Label } from '../ui/label';  
import { X, Plus, Minus, ArrowUp, ArrowDown } from 'lucide-react';  

type Topic = {  
  id?: string;  
  topicName?: string;  
  topicChips: any[]; // Added topicChips to each Topic  
}  

type TopicChip = {  
  id: string; // Ensure this is defined  
  text: string;  
}  

interface TopicsListProps {  
  topics: any[]; // Updated type to reflect individual topic structure  
  onTopicsChange: (topics: any[]) => void;  
}  

const TopicsList = ({ topics, onTopicsChange }: TopicsListProps) => {  
  const [newTopic, setNewTopic] = useState('');  
  const [newTopicChip, setNewTopicChip] = useState<string | null>(null);  
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null); // Track selected topic for adding chips  

  const addTopic = () => {  
    if (!newTopic.trim()) return;  

    const newTopicItem: Topic = {  
      id: Date.now().toString(), // Generate a new unique ID based on timestamp  
      topicName: newTopic,  
      topicChips: [] // Initialize with an empty array for topicChips  
    };  
    onTopicsChange([...topics, newTopicItem]);  
    setNewTopic('');  
  };  

  const removeTopic = (id: string) => {  
    onTopicsChange(topics.filter(topic => topic.id !== id));  
  };  

  const addTopicChip = (topicId: string) => {  
    if (!newTopicChip?.trim()) return;  

    const newChip: TopicChip = {  
      id: Date.now().toString(), // Use ID based on timestamp to ensure uniqueness  
      text: newTopicChip  
    };  

    const updatedTopics = topics.map(topic => {  
      if (topic.id === topicId) {  
        return { ...topic, topicChips: [...topic.topicChips, newChip] }; // Add to the correct topic's chips  
      }  
      return topic;  
    });  

    onTopicsChange(updatedTopics);  
    setNewTopicChip('');  
  };  

  const removeTopicChip = (topicId: string, chipId: string) => {  
    const updatedTopics = topics.map(topic => {  
      if (topic.id === topicId) {  
        return { ...topic, topicChips: topic.topicChips.filter(chip => chip.id !== chipId) };  
      }  
      return topic;  
    });  

    onTopicsChange(updatedTopics);  
  };  

  const moveChipUp = (topicId: string, index: number) => {  
    if (index === 0) return;  

    const updatedTopics = topics.map(topic => {  
      if (topic.id === topicId) {  
        const newChips = [...topic.topicChips];  
        [newChips[index], newChips[index - 1]] = [newChips[index - 1], newChips[index]];  
        return { ...topic, topicChips: newChips };  
      }  
      return topic;  
    });  

    onTopicsChange(updatedTopics);  
  };  

  const moveChipDown = (topicId: string, index: number) => {  
    if (index === topics.find(topic => topic.id === topicId)?.topicChips.length - 1) return;  

    const updatedTopics = topics.map(topic => {  
      if (topic.id === topicId) {  
        const newChips = [...topic.topicChips];  
        [newChips[index], newChips[index + 1]] = [newChips[index + 1], newChips[index]];  
        return { ...topic, topicChips: newChips };  
      }  
      return topic;  
    });  

    onTopicsChange(updatedTopics);  
  };  

  return (  
    <div className="space-y-4 border rounded-lg p-4">  
      <div className="flex items-center justify-between">  
        <h3 className="text-lg font-semibold text-bytesplash-blue">Module Topics</h3>  
        <Button  
          type="button"  
          variant="outline"  
          size="sm"  
          onClick={addTopic}  
        >  
          <Plus className="mr-1 h-4 w-4" />  
          Add Module Topic  
        </Button>  
      </div>  

      {topics?.map((topic, topicIndex) => (  
        <div key={topic.id} className="space-y-4 border-b pb-4 last:border-0">  
          <div className="flex items-center gap-3">  
            <span className="text-bytesplash-blue font-medium">  
              Topic {topicIndex + 1}  
            </span>  
            <Button  
              type="button"  
              variant="ghost"  
              size="sm"  
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"  
              onClick={() => removeTopic(topic.id)}  
            >  
              <Minus className="h-4 w-4" />  
            </Button>  
          </div>  

          <div className="space-y-2">  
            <Label htmlFor={`topic-heading-${topic.id}`} className="text-sm">  
              Topic Heading<span className="text-red-500">*</span>  
            </Label>  
            <Input  
              id={`topic-heading-${topic.id}`}  
              value={topic.topicName}  
              onChange={(e) => {  
                const updatedTopics = topics.map(t =>  
                  t.id === topic.id ? { ...t, topicName: e.target.value } : t  
                );  
                onTopicsChange(updatedTopics);  
              }}  
              placeholder="e.g., Introduction to HTML"  
            />  
          </div>  

          <div className="space-y-2">  
            <Label className="text-sm">  
              Add Topics  
            </Label>  
            <div className="flex gap-2">  
              <Input  
                value={newTopicChip}  
                onChange={(e) => setNewTopicChip(e.target.value)}  
                placeholder="e.g., History of HTML"  
                className="flex-1"  
                onKeyDown={(e) => {  
                  if (e.key === 'Enter' && newTopicChip.trim()) {  
                    e.preventDefault();  
                    addTopicChip(topic.id); // Pass the current topic ID  
                  }  
                }}  
              />  
              <Button  
                type="button"  
                variant="outline"  
                onClick={() => addTopicChip(topic.id)} // Pass the current topic ID  
                disabled={!newTopicChip?.trim()}  
              >  
                <Plus className="h-4 w-4" />  
              </Button>  
            </div>  
          </div>  

          <div className="flex flex-wrap gap-2 mt-2">  
            {topic?.topicChips?.map((chip, chipIndex) => (  
              <div key={chip.id} className="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-sm">  
                <span className="max-w-[250px] truncate">{chip.text}</span>  
                <div className="flex gap-1 ml-1">  
                  <button  
                    type="button"  
                    className="text-gray-500 hover:text-bytesplash-blue"  
                    onClick={() => moveChipUp(topic.id, chipIndex)}  
                    disabled={chipIndex === 0}  
                  >  
                    <ArrowUp className="h-3 w-3" />  
                  </button>  
                  <button  
                    type="button"  
                    className="text-gray-500 hover:text-bytesplash-blue"  
                    onClick={() => moveChipDown(topic.id, chipIndex)}  
                    disabled={chipIndex === topic.topicChips.length - 1}  
                  >  
                    <ArrowDown className="h-3 w-3" />  
                  </button>  
                  <button  
                    type="button"  
                    className="text-gray-500 hover:text-destructive"  
                    onClick={() => removeTopicChip(topic.id, chip.id)} // Pass both IDs  
                  >  
                    <X className="h-3 w-3" />  
                  </button>  
                </div>  
              </div>  
            ))}  
          </div>  
        </div>  
      ))}  

      {(  
        <div className="text-center py-8 border rounded-md bg-gray-50">  
          {topics?.length === 0 && <p className="text-gray-500 mb-2">No topics added yet.</p>}  
          <div className="flex gap-2 justify-center">  
            <Input  
              value={newTopic}  
              onChange={(e) => setNewTopic(e.target.value)}  
              placeholder="Topic name"  
              className="max-w-sm"  
            />  
            <Button  
              type="button"  
              variant="outline"  
              onClick={addTopic}  
              disabled={!newTopic.trim()}  
            >  
              <Plus className="mr-1 h-4 w-4" />  
              Add Topic  
            </Button>  
          </div>  
        </div>  
      )}  
    </div>  
  );  
};  

export default TopicsList;  